import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Inbox,
  Archive,
  UserCheck,
  AlarmClock,
  Bot,
  Building2,
  Globe,
  Users,
  Zap,
  BarChart3,
  Sparkles,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronRight,
  CircleAlert,
  Settings,
  Bell,
  Folder,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Check,
  X,
  Archive as ArchiveIcon,
} from "lucide-react";
import { cn } from "../../utils/cn";
import usePersistentState from "../../hooks/usePersistentState";
import useAuth from "../../hooks/useAuth";
import { roleRoutes } from "../../utils/roles";
import {
  SEVERITY_TONE,
  describeDisconnect,
  formatDisconnectTime,
  isSessionDown,
  reasonShortText,
  reasonText,
} from "../../utils/waDisconnect";
import {
  FOLDER_COLOR_STYLES,
  DEFAULT_FOLDER_COLOR,
  FOLDER_COLOR_KEYS,
  FOLDER_COLOR_LABELS,
} from "../../components/helpdesk/folderColors";

// Ruta de la vista del asistente interno ("Asistente interno", sección Suite).
// Vive acá y no en routes.jsx porque importar el árbol de rutas arrastraría
// TODAS las vistas al bundle del rail; el ítem de Suite y la bandeja del canal
// comparten esta constante para no tener dos literales que se puedan separar.
export const INTERNAL_ASSISTANT_PATH = "/admin/helpdesk/interno";

/**
 * ¿La bandeja del canal "Interno" se pinta en la sección de canales?
 * - `counters.internal > 0`: hay conversaciones del canal en la lista cargada
 *   (el contador sale de esa lista, así que fuera de su bandeja queda en 0).
 * - O la vista del asistente interno está cableada en este build. Ese operando
 *   es hoy SIEMPRE verdadero (la ruta existe, es la misma que navega el ítem de
 *   Suite), y es a propósito: sin la bandeja, la vista solo se descubriría
 *   entrando por URL — que es justo lo que el ítem debería estar anunciando.
 *   El día que esa vista se retire, borrar la constante deja la bandeja
 *   dependiendo SOLO del contador, sin tocar el render.
 * @param {{internal?: number}} [counters]
 * @returns {boolean}
 */
export function showInternalChannelTray(counters = {}) {
  return (counters?.internal ?? 0) > 0 || !!INTERNAL_ASSISTANT_PATH;
}

// Ícono SVG inline para canales que no están en lucide.
const ChannelIcon = ({ kind, className = "w-4 h-4" }) => {
  if (kind === "whatsapp") {
    return (
      <svg viewBox="0 0 24 24" className={cn(className, "fill-current")} aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    );
  }
  if (kind === "messenger") {
    return (
      <svg viewBox="0 0 24 24" className={cn(className, "fill-current")} aria-hidden="true">
        <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111C24 4.975 18.627 0 12 0zm1.193 14.963l-3.056-3.259-5.963 3.259L10.733 8.4l3.13 3.259 5.889-3.259-6.56 6.563z" />
      </svg>
    );
  }
  if (kind === "instagram") {
    return (
      <svg viewBox="0 0 24 24" className={cn(className, "fill-current")} aria-hidden="true">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    );
  }
  return null;
};

// ─── Cuentas por canal ───────────────────────────────────────────────────────
// Un comercio puede tener varios WhatsApp, varias páginas de Messenger, etc., y
// el agente necesita saber por cuál número está atendiendo. El backend devuelve
// esas cuentas en /conversations/channel-accounts; si el endpoint falla,
// `channelAccounts` llega vacío y esta sección desaparece por completo (sin
// sub-árbol, sin chevrons, sin espacio muerto).

// Qué canales del payload alimentan cada ítem del rail. WhatsApp agrupa oficial
// y no oficial, igual que el fetch del inbox.
export const CHANNEL_ACCOUNT_KINDS = {
  "channel.whatsapp": ["whatsapp", "whatsapp_unofficial"],
  "channel.messenger": ["messenger"],
  "channel.instagram": ["instagram"],
  "channel.portal": ["portal"],
};

/**
 * Identidad estable de una cuenta. El orden importa: la clave se calcula tanto
 * desde el listado de cuentas del rail como desde conversation.channel_account,
 * y ese segundo objeto solo trae {label, phone, instance_name} — nunca `id`.
 * Por eso `id` va último: si mandara, los canales Meta (que no tienen
 * instance_name) producirían una clave que la conversación no puede reproducir
 * y el filtro por cuenta dejaría la bandeja vacía.
 * @param {object} account
 * @returns {string|null}
 */
export function accountKeyOf(account) {
  if (!account) return null;
  return (
    account.instance_name ||
    account.phone ||
    account.label ||
    account.id ||
    null
  );
}

// Prefijos internacionales que nos interesan. Se prueban de más largo a más
// corto para que 593 (Ecuador) gane a 59, y 56 (Chile) a 5.
const DIAL_CODES = [
  "591", "592", "593", "594", "595", "596", "597", "598", "599",
  "501", "502", "503", "504", "505", "506", "507", "508", "509",
  "351", "352", "353", "358", "372", "380",
  "20", "27", "30", "31", "32", "33", "34", "36", "39",
  "40", "41", "43", "44", "45", "46", "47", "48", "49",
  "51", "52", "53", "54", "55", "56", "57", "58",
  "60", "61", "62", "63", "64", "65", "66",
  "81", "82", "84", "86", "90", "91", "92", "93", "94", "95", "98",
  "1", "7",
].sort((a, b) => b.length - a.length);

// Agrupa de derecha a izquierda en bloques de 4: 912345678 → "9 1234 5678",
// 5551234567 → "55 5123 4567". Una sola regla cubre los largos que vemos en la
// operación sin mantener un plan de numeración por país.
function groupFromRight(digits, size = 4) {
  const parts = [];
  let cut = digits.length;
  while (cut > size) {
    parts.unshift(digits.slice(cut - size, cut));
    cut -= size;
  }
  parts.unshift(digits.slice(0, cut));
  return parts;
}

/**
 * Número legible a partir de lo que mande el backend (E.164, JID sin "+", o ya
 * formateado por el proveedor). Si ya trae separadores se respeta tal cual: el
 * dato humano suele ser mejor que cualquier heurística nuestra.
 * @param {string|number|null|undefined} raw
 * @returns {string|null}
 */
export function formatPhone(raw) {
  if (raw === null || raw === undefined) return null;
  // WhatsApp puede mandar el JID crudo (593987654321@s.whatsapp.net): el sufijo
  // es ruido de transporte y además dispararía el guard de "ya formateado".
  const value = String(raw).trim().split("@")[0].trim();
  if (!value) return null;
  if (/[\s\-().]/.test(value)) return value; // ya viene formateado
  const digits = value.replace(/\D/g, "");
  if (!digits) return value;
  // Sin "+" pero con 11+ dígitos es un JID de WhatsApp (56912345678).
  const international = value.startsWith("+") || digits.length >= 11;
  if (international) {
    const code = DIAL_CODES.find(
      (c) => digits.startsWith(c) && digits.length > c.length,
    );
    if (code) return `+${code} ${groupFromRight(digits.slice(code.length)).join(" ")}`;
    return `+${groupFromRight(digits).join(" ")}`;
  }
  if (digits.length < 7) return value; // extensiones / ids cortos: no tocar
  return groupFromRight(digits).join(" ");
}

// Estados en los que la cuenta está realmente operativa. `open` es el que usa
// Evolution para WhatsApp no oficial; el resto cubre variantes de los canales
// Meta y del portal.
const ACTIVE_STATUSES = new Set([
  "open",
  "active",
  "connected",
  "online",
  "ready",
  "authorized",
  "enabled",
  "ok",
]);

const STATUS_LABELS = {
  connecting: "Conectando",
  pending: "Conectando",
  inactive: "Desconectado",
  degraded: "Con intermitencia",
  close: "Desconectado",
  closed: "Desconectado",
  disconnected: "Desconectado",
  expired: "Sesión expirada",
  error: "Con error",
};

/**
 * Cómo se ve la cuenta en el rail: qué texto lleva arriba, cuál abajo y si hay
 * algo que advertir. `phone` puede venir undefined mientras el backend termina
 * de poblarlo: en ese caso la fila muestra solo el nombre, sin hueco ni guion.
 * @param {object} account
 * `disconnect` explica por qué se cayó la sesión (bloqueo, sesión cerrada…):
 * solo con la sesión caída, nunca mientras conecta o ya está conectada.
 * @returns {{primary: string, secondary: string|null, notificationsOnly: boolean, inactive: boolean, statusLabel: string|null, disconnect: ReturnType<typeof describeDisconnect>, count: number|null}}
 */
function describeAccount(account) {
  const phone = formatPhone(account?.phone);
  const rawName = account?.label || account?.instance_name || null;
  // Si el nombre ES el número, no lo repetimos en dos líneas.
  const name =
    rawName && rawName !== account?.phone && rawName !== phone ? rawName : null;

  // La sesión (wa_connection_status: open/close/connecting/degraded) manda
  // sobre el ciclo de vida de la integración (`status`): una integración
  // activa puede tener la sesión caída, y eso es lo que el agente necesita ver.
  const rawStatus =
    account?.wa_connection_status ?? account?.status ?? account?.connection_status;
  const status = typeof rawStatus === "string" ? rawStatus.trim().toLowerCase() : null;
  // Sin dato de estado no inventamos un problema: se asume operativa.
  const inactive =
    typeof rawStatus === "boolean" ? !rawStatus : !!status && !ACTIVE_STATUSES.has(status);
  const statusLabel = inactive ? STATUS_LABELS[status] || "Inactiva" : null;
  // Only an outage that is still going on explains the current state.
  const lastDisconnect =
    inactive && isSessionDown(status) ? describeDisconnect(account?.last_disconnect) : null;
  const disconnect = lastDisconnect?.ongoing ? lastDisconnect : null;

  const purpose = typeof account?.purpose === "string" ? account.purpose.trim().toLowerCase() : "";
  const notificationsOnly = purpose === "notifications";

  // Contador solo si ya viene en el payload. No se estima nada.
  const count = [
    account?.unread_count,
    account?.unread,
    account?.conversations_count,
    account?.count,
  ].find((value) => typeof value === "number" && Number.isFinite(value));

  return {
    primary: phone || name || account?.id || "—",
    secondary: phone ? name : null,
    notificationsOnly,
    inactive,
    statusLabel,
    disconnect,
    count: typeof count === "number" ? count : null,
  };
}

// ─── Item del rail ───────────────────────────────────────────────────────────
function RailItem({
  icon: Icon,
  channelKind,
  label,
  isActive,
  counter,
  badgeTone,
  disabled,
  hint,
  collapsed,
  onClick,
}) {
  const hasCounter = typeof counter === "number" && counter > 0;
  // Colapsado no hay texto: el tooltip (y el aria-label) cargan nombre y
  // contador, que si no quedarían invisibles.
  const collapsedTitle = [label, hasCounter ? `(${counter})` : null, disabled && hint ? `(${hint})` : null]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <span
        className={cn(
          "shrink-0 inline-flex w-4 h-4 items-center justify-center",
          // Único acento del ítem activo: el ícono (el fondo queda neutro).
          isActive && !disabled && "text-brand-700",
        )}
      >
        {Icon ? <Icon className="w-4 h-4" aria-hidden="true" /> : <ChannelIcon kind={channelKind} />}
      </span>
      {!collapsed && (
        <>
          <span className="flex-1 truncate text-left">{label}</span>
          {hasCounter && (
            // Contador como texto tabular, sin pill: "99+" en una pill sólida
            // roja/naranja era lo más ruidoso del rail. El tono solo se
            // conserva para lo que exige atención (SLA vencido, alertas).
            <span
              className={cn(
                "ml-2 shrink-0 text-[12px] leading-none tabular-nums",
                badgeTone === "danger"
                  ? "text-status-error font-semibold"
                  : badgeTone === "warning"
                  ? "text-status-warning font-semibold"
                  : isActive
                  ? "text-fg font-medium"
                  : "text-fg-muted",
              )}
            >
              {counter > 999 ? "999+" : counter}
            </span>
          )}
          {disabled && hint && (
            // shrink-0 + nowrap: sin esto el label (flex-1) comprimía el hint y
            // "Pronto" se partía en dos líneas ("PRON / TO").
            <span className="ml-2 shrink-0 whitespace-nowrap text-[11px] text-fg-subtle uppercase tracking-wide">
              {hint}
            </span>
          )}
        </>
      )}
      {/* Colapsado, un contador >0 sigue siendo información: punto discreto. */}
      {collapsed && hasCounter && !disabled && (
        <span
          className={cn(
            "absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full",
            badgeTone === "danger"
              ? "bg-status-error"
              : badgeTone === "warning"
              ? "bg-status-warning"
              : "bg-brand-500",
          )}
          aria-hidden="true"
        />
      )}
    </>
  );

  const baseClass = cn(
    "group relative w-full flex items-center gap-2.5 h-11 md:h-8 px-2 rounded-md text-[13px] transition-colors",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
    collapsed && "justify-center px-0",
    disabled
      ? "text-fg-subtle cursor-not-allowed"
      : isActive
      ? "bg-bg-subtle text-fg font-medium"
      : "text-fg-muted hover:bg-bg-muted hover:text-fg",
  );

  if (disabled) {
    return (
      <button
        type="button"
        disabled
        className={baseClass}
        title={collapsed ? collapsedTitle : undefined}
        aria-label={collapsed ? collapsedTitle : undefined}
      >
        {content}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      className={baseClass}
      title={collapsed ? collapsedTitle : undefined}
      aria-label={collapsed ? collapsedTitle : undefined}
    >
      {content}
    </button>
  );
}

// ─── Canal con sub-árbol de cuentas ─────────────────────────────────────────
function RailChannel({
  navId,
  channelKey,
  icon,
  channelKind,
  label,
  isActive,
  counter,
  collapsed,
  accounts,
  expanded,
  onToggleExpanded,
  accountFilter,
  onSelect,
  onSelectAccount,
  activeKey,
  onSelectRetired,
  onManageAccount,
}) {
  const { t, i18n } = useTranslation();
  // Los números RETIRADOS sin reemplazo no son cuentas de trabajo: sus hilos
  // están fuera de las colas y se ven en su propio grupo, plegado al final.
  const liveAccounts = accounts.filter((account) => !account?.retired);
  const retiredAccounts = accounts.filter((account) => account?.retired);
  // Basta UNA cuenta para desplegar: aunque el comercio atienda por un solo
  // número, el agente necesita ver por cuál está respondiendo. Colapsado no hay
  // sitio para el sub-árbol, así que el canal queda como ítem simple (con su
  // tooltip, que es donde vive el nombre cuando no hay texto).
  const expandable = !collapsed && accounts.length > 0;
  // Con más de una cuenta tiene sentido un "Todas"; con una sola, ese "Todas"
  // sería un clon de la fila del canal (que ya limpia el filtro).
  const showAllRow = liveAccounts.length > 1;
  // Las cuentas solo se marcan como seleccionadas si el rail está parado en
  // ESTE canal: si no, la bandeja mostraría "Sin asignar" mientras el sub-árbol
  // de WhatsApp asegura estar en "Todas las cuentas".
  const allSelected = isActive && !accountFilter;
  // El id cuelga del navId de la instancia: el shell monta DOS rails a la vez
  // (el desktop queda en el DOM aunque `hidden md:flex` lo oculte), y con un id
  // fijo los aria-controls del drawer resolverían al rail invisible.
  const panelId = `${navId}-accounts-${channelKey.replace(/\./g, "-")}`;

  const item = (
    <RailItem
      icon={icon}
      channelKind={channelKind}
      label={label}
      isActive={isActive}
      counter={counter}
      collapsed={collapsed}
      onClick={onSelect}
    />
  );

  if (!expandable) return item;

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-0.5">
        <div className="flex-1 min-w-0">{item}</div>
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={
            expanded
              ? t("Ocultar cuentas de {{canal}}").replace("{{canal}}", label)
              : t("Ver cuentas de {{canal}}").replace("{{canal}}", label)
          }
          title={
            expanded
              ? t("Ocultar cuentas de {{canal}}").replace("{{canal}}", label)
              : t("Ver cuentas de {{canal}}").replace("{{canal}}", label)
          }
          className="shrink-0 inline-flex items-center justify-center w-11 h-11 md:w-6 md:h-8 rounded-md text-fg-subtle hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
        >
          <ChevronRight
            className={cn(
              "w-3.5 h-3.5 transition-transform duration-200 ease-out motion-reduce:transition-none",
              expanded && "rotate-90",
            )}
            aria-hidden="true"
          />
        </button>
      </div>

      {expanded && (
        <div
          id={panelId}
          role="group"
          aria-label={t("Cuentas de {{canal}}").replace("{{canal}}", label)}
          className={cn(
            // Guía vertical alineada con el centro del ícono del canal (px-2 +
            // media caja de 16px): las cuentas se leen como hijas, no como
            // ítems hermanos con sangría.
            "flex flex-col gap-0.5 ml-4 pl-2 border-l border-border",
            "animate-fade-in motion-reduce:animate-none",
          )}
        >
          {showAllRow && (
            <button
              type="button"
              onClick={() => onSelectAccount(channelKey, null)}
              aria-current={allSelected ? "true" : undefined}
              className={cn(
                "w-full flex items-center gap-2 min-h-11 md:min-h-[28px] py-1 px-2 rounded-md text-[12px]",
                "transition-colors duration-150 ease-out motion-reduce:transition-none",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                allSelected
                  ? "bg-bg-subtle text-fg font-medium"
                  : "text-fg-subtle hover:bg-bg-muted hover:text-fg",
              )}
            >
              <span className="truncate">{t("Todas las cuentas")}</span>
            </button>
          )}
          {liveAccounts.map((account, index) => {
            const key = accountKeyOf(account);
            const isAccountActive = isActive && !!key && key === accountFilter;
            const {
              primary,
              secondary,
              notificationsOnly,
              inactive,
              statusLabel,
              disconnect,
              count,
            } = describeAccount(account);
            // Bloqueo = error; el resto de las caídas sigue en advertencia.
            const downTone =
              disconnect?.severity === "error" ? SEVERITY_TONE.error : SEVERITY_TONE.warning;
            const disconnectedAt = disconnect?.at
              ? formatDisconnectTime(disconnect.at, i18n?.language, { compact: true })
              : "";
            const sinceText = disconnectedAt
              ? t("Caído desde {{fecha}}", { fecha: disconnectedAt })
              : null;
            const chipText = reasonShortText(disconnect, t) || (statusLabel ? t(statusLabel) : null);

            // El estado nunca queda solo en el color: el número que no conversa
            // lleva ícono de campana (forma distinta), la fila apagada baja de
            // contraste, y ambos casos exponen texto para lector de pantalla y
            // tooltip.
            const stateParts = [
              notificationsOnly
                ? t("Solo notificaciones: no recibe conversaciones")
                : null,
              statusLabel ? t(statusLabel) : t("Número conectado"),
              disconnect ? reasonText(disconnect, t) : null,
              sinceText,
            ].filter(Boolean);
            const stateText = stateParts.join(" · ");
            const title = [primary, secondary, stateText].filter(Boolean).join(" · ");
            const dimmed = (inactive || notificationsOnly) && !isAccountActive;
            // Retirar o migrar: solo números no oficiales caídos (uno conectado
            // se gestiona desde Canales) y solo para quien puede hacerlo.
            const manageable =
              !!onManageAccount && inactive && account?.channel === "whatsapp_unofficial";

            return (
              <div key={key || `${primary}-${index}`} className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => onSelectAccount(channelKey, account)}
                aria-current={isAccountActive ? "true" : undefined}
                title={title}
                className={cn(
                  "flex-1 min-w-0 flex items-center gap-2 min-h-11 md:min-h-[28px] py-1 px-2 rounded-md text-left",
                  "transition-colors duration-150 ease-out motion-reduce:transition-none",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                  isAccountActive
                    ? "bg-bg-subtle text-fg font-medium"
                    : dimmed
                    ? "text-fg-subtle hover:bg-bg-muted hover:text-fg"
                    : "text-fg-muted hover:bg-bg-muted hover:text-fg",
                )}
              >
                <span
                  className="shrink-0 inline-flex w-3 h-3 items-center justify-center"
                  aria-hidden="true"
                >
                  {notificationsOnly ? (
                    <Bell
                      className={cn(
                        "w-3 h-3",
                        inactive ? downTone.text : "text-fg-subtle",
                      )}
                    />
                  ) : (
                    <span
                      className={cn(
                        "w-1.5 h-1.5 rounded-full",
                        inactive ? downTone.dot : "bg-status-success",
                      )}
                    />
                  )}
                </span>

                <span className="flex-1 min-w-0">
                  <span className="block truncate text-[12px] leading-tight tabular-nums">
                    {primary}
                  </span>
                  {/* Sin teléfono todavía, `secondary` es null y la fila queda
                      de una línea: ni hueco reservado ni guion de relleno. El
                      estado va en esta segunda línea para no recortar el número:
                      se lee "Desconectado", no solo se colorea (el punto
                      verde/ámbar es refuerzo). */}
                  {(secondary || chipText) && (
                    <span className="flex items-center gap-1 min-w-0 text-[11px] leading-tight font-normal text-fg-subtle">
                      {secondary && <span className="truncate">{secondary}</span>}
                      {chipText && (
                        // Con motivo conocido el chip dice POR QUÉ ("Bloqueado",
                        // "Sesión cerrada") en vez del genérico "Desconectado";
                        // el texto completo va en el tooltip y el sr-only.
                        <span
                          className={cn(
                            "shrink-0 inline-flex items-center px-1.5 h-[14px] rounded-full font-medium whitespace-nowrap",
                            downTone.chip,
                          )}
                          aria-hidden="true"
                        >
                          {chipText}
                        </span>
                      )}
                    </span>
                  )}
                  {sinceText && (
                    <span
                      className="block mt-0.5 text-[11px] leading-tight font-normal text-fg-subtle tabular-nums"
                      aria-hidden="true"
                    >
                      {sinceText}
                    </span>
                  )}
                </span>

                {typeof count === "number" && count > 0 && (
                  <span
                    className={cn(
                      "shrink-0 text-[12px] leading-none tabular-nums",
                      isAccountActive ? "text-fg font-medium" : "text-fg-muted",
                    )}
                  >
                    {count > 999 ? "999+" : count}
                  </span>
                )}

                {stateText && <span className="sr-only">{stateText}</span>}
              </button>
              {manageable && (
                <ManageAccountButton
                  label={t("Retirar o migrar {{numero}}").replace("{{numero}}", primary)}
                  onClick={() => onManageAccount(account)}
                />
              )}
              </div>
            );
          })}

          {retiredAccounts.length > 0 && (
            <RetiredAccountsGroup
              navId={navId}
              accounts={retiredAccounts}
              activeKey={activeKey}
              onSelect={onSelectRetired}
              onManageAccount={onManageAccount}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Botón secundario de una fila de cuenta (al lado, no adentro: un botón dentro
// de otro es HTML inválido y el lector de pantalla no lo anuncia).
function ManageAccountButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="shrink-0 inline-flex items-center justify-center w-11 h-11 md:w-6 md:h-7 rounded-md text-fg-subtle hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
    >
      <MoreHorizontal className="w-3.5 h-3.5" aria-hidden="true" />
    </button>
  );
}

/**
 * Números retirados sin reemplazo, plegados por defecto: no son trabajo
 * diario. Cada uno abre su propia vista (`retired.<id>`) con las conversaciones
 * que quedaron abiertas; el contador es de abiertas.
 */
function RetiredAccountsGroup({ navId, accounts, activeKey, onSelect, onManageAccount }) {
  const { t } = useTranslation();
  const hasActive = accounts.some((account) => activeKey === `retired.${account.id}`);
  const [open, setOpen] = useState(hasActive);
  // Si se llega a una vista de retirado (enlace, otra ruta) el grupo se abre.
  useEffect(() => {
    if (hasActive) setOpen(true);
  }, [hasActive]);
  const panelId = `${navId}-retired-accounts`;

  return (
    <div className="flex flex-col gap-0.5 mt-1">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center gap-1.5 min-h-11 md:min-h-[28px] py-1 px-2 rounded-md text-[11px] font-medium text-fg-subtle hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <ChevronRight
          className={cn(
            "w-3 h-3 shrink-0 transition-transform duration-200 ease-out motion-reduce:transition-none",
            open && "rotate-90",
          )}
          aria-hidden="true"
        />
        <span className="flex-1 text-left truncate">
          {t("Números retirados ({{n}})").replace("{{n}}", String(accounts.length))}
        </span>
      </button>
      {open && (
        <div id={panelId} role="group" aria-label={t("Números retirados")} className="flex flex-col gap-0.5">
          {accounts.map((account) => {
            const { primary, secondary } = describeAccount(account);
            const isRowActive = activeKey === `retired.${account.id}`;
            const openCount = typeof account.open_count === "number" ? account.open_count : 0;
            const title = [
              primary,
              secondary,
              t("Retirado"),
              t("{{n}} conversaciones abiertas").replace("{{n}}", String(openCount)),
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <div key={account.id} className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => onSelect?.(account)}
                  aria-current={isRowActive ? "true" : undefined}
                  title={title}
                  className={cn(
                    "flex-1 min-w-0 flex items-center gap-2 min-h-11 md:min-h-[28px] py-1 px-2 rounded-md text-left",
                    "transition-colors duration-150 ease-out motion-reduce:transition-none",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                    isRowActive
                      ? "bg-bg-subtle text-fg font-medium"
                      : "text-fg-subtle hover:bg-bg-muted hover:text-fg",
                  )}
                >
                  <ArchiveIcon className="w-3 h-3 shrink-0" aria-hidden="true" />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-[12px] leading-tight tabular-nums">
                      {primary}
                    </span>
                    {secondary && (
                      <span className="block truncate text-[11px] leading-tight font-normal">
                        {secondary}
                      </span>
                    )}
                  </span>
                  {openCount > 0 && (
                    <span
                      className="shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[11px] font-semibold tabular-nums bg-bg-subtle text-fg-muted"
                      aria-hidden="true"
                    >
                      {openCount > 999 ? "999+" : openCount}
                    </span>
                  )}
                  <span className="sr-only">{title}</span>
                </button>
                {onManageAccount && (
                  <ManageAccountButton
                    label={t("Migrar {{numero}} a otro número").replace("{{numero}}", primary)}
                    onClick={() => onManageAccount(account)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Carpetas ────────────────────────────────────────────────────────────────
// Carpetas manuales del tenant para ordenar la bandeja. El backend impone
// nombre único y tope de 30 (ambos responden 409): esos errores se muestran
// INLINE en el editor, no como fallo genérico.

// Traduce el error del guardado a un mensaje accionable. El 409 distingue
// "duplicado" de "tope de 30" solo por el texto del backend, así que se
// olfatea; si no se reconoce, gana el mensaje del servidor.
function folderErrorMessage(err, t) {
  if (err?.status === 409) {
    return /30|m[aá]x|limit/i.test(err?.message || "")
      ? t("Máximo 30 carpetas")
      : t("Ya existe una carpeta con ese nombre");
  }
  return err?.message || t("No se pudo guardar la carpeta");
}

const MAX_FOLDER_NAME = 40;

// Editor inline de carpeta (crear y renombrar comparten esto). El estado del
// borrador vive ACÁ: teclear el nombre no re-renderiza el rail entero.
function FolderEditor({ initialName = "", initialColor = DEFAULT_FOLDER_COLOR, onSave, onCancel }) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [color, setColor] = useState(initialColor || DEFAULT_FOLDER_COLOR);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const rootRef = useRef(null);

  const trimmed = name.trim();

  const handleSave = async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed, color);
    } catch (err) {
      setError(folderErrorMessage(err, t));
      setSaving(false);
      return;
    }
    // onSave cierra el editor desde el padre: no hay setState tras desmontar.
  };

  return (
    <div
      ref={rootRef}
      className="px-2 py-1.5 rounded-md bg-bg-muted/50 flex flex-col gap-1.5"
      // Blur con el borrador vacío = cancelar. Se mide contra el CONTENEDOR
      // (relatedTarget): pinchar un dot de color también dispara el blur del
      // input y no debe cerrar nada.
      onBlur={(e) => {
        if (
          !e.currentTarget.contains(e.relatedTarget) &&
          !name.trim() &&
          !saving
        ) {
          onCancel();
        }
      }}
    >
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSave();
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
            }
          }}
          maxLength={MAX_FOLDER_NAME}
          autoFocus
          disabled={saving}
          placeholder={t("Nombre de la carpeta")}
          aria-label={t("Nombre de la carpeta")}
          // Variantes !: _modern-forms.scss pisa los input[type="text"] con más
          // especificidad que las utilidades de Tailwind.
          className="flex-1 min-w-0 !h-11 md:!h-7 !text-[12px] px-2 rounded-md border border-border bg-bg text-fg placeholder:text-fg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !trimmed}
          aria-label={t("Guardar carpeta")}
          title={t("Guardar (Enter)")}
          className="shrink-0 inline-flex w-11 h-11 md:w-7 md:h-7 items-center justify-center rounded-md text-brand-600 hover:bg-bg-muted disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          {saving ? (
            <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin motion-reduce:animate-none" />
          ) : (
            <Check className="w-3.5 h-3.5" aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          aria-label={t("Cancelar")}
          title={t("Cancelar (Esc)")}
          className="shrink-0 inline-flex w-11 h-11 md:w-7 md:h-7 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </div>

      {/* Paleta fija de 5 colores (tokens de status). El color es refuerzo,
          nunca la única señal: el nombre siempre acompaña al dot. */}
      <div className="flex items-center gap-1.5" role="radiogroup" aria-label={t("Color de la carpeta")}>
        {FOLDER_COLOR_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={color === key}
            aria-label={t(FOLDER_COLOR_LABELS[key])}
            title={t(FOLDER_COLOR_LABELS[key])}
            onClick={() => setColor(key)}
            disabled={saving}
            className={cn(
              "inline-flex w-11 h-11 md:w-5 md:h-5 items-center justify-center rounded-full",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
              color === key && "ring-2 ring-brand-500 ring-offset-1 ring-offset-bg",
            )}
          >
            <span
              className={cn("w-3 h-3 rounded-full", FOLDER_COLOR_STYLES[key].dot)}
              aria-hidden="true"
            />
          </button>
        ))}
      </div>

      {error && (
        <p role="alert" className="text-[11px] text-status-error">
          {error}
        </p>
      )}
    </div>
  );
}

// Fila de carpeta del rail. Mismo lenguaje que RailItem (h-11 md:h-8, activa
// bg-brand-50 text-brand-700) + menú "⋯" propio (Renombrar / Eliminar).
function FolderRailRow({ folder, isActive, collapsed, onSelect, onStartRename, onRequestDelete }) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClick = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (menuBtnRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  const styles =
    FOLDER_COLOR_STYLES[folder.color] || FOLDER_COLOR_STYLES[DEFAULT_FOLDER_COLOR];
  const count = typeof folder.count === "number" ? folder.count : 0;
  const hasCounter = count > 0;
  const collapsedTitle = `${folder.name}${hasCounter ? ` (${count})` : ""}`;

  if (collapsed) {
    // Colapsado: botón centrado con el ícono de carpeta y el dot de color
    // superpuesto. Sin "⋯" ni edición — para eso se expande el rail.
    return (
      <button
        type="button"
        onClick={onSelect}
        aria-current={isActive ? "page" : undefined}
        title={collapsedTitle}
        aria-label={collapsedTitle}
        className={cn(
          "group relative w-full flex items-center justify-center h-11 md:h-8 rounded-md text-sm transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
          isActive
            ? "bg-bg-subtle text-fg font-medium"
            : "text-fg-muted hover:bg-bg-muted hover:text-fg",
        )}
      >
        <Folder className="w-4 h-4" aria-hidden="true" />
        <span
          className={cn("absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full", styles.dot)}
          aria-hidden="true"
        />
      </button>
    );
  }

  return (
    <div className="relative flex items-center gap-0.5">
      <button
        type="button"
        onClick={onSelect}
        aria-current={isActive ? "page" : undefined}
        title={folder.name}
        className={cn(
          "flex-1 min-w-0 flex items-center gap-2.5 h-11 md:h-8 px-2 rounded-md text-sm transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
          isActive
            ? "bg-bg-subtle text-fg font-medium"
            : "text-fg-muted hover:bg-bg-muted hover:text-fg",
        )}
      >
        {/* Dot de color en el hueco del ícono, centrado como los w-4 de RailItem */}
        <span className="shrink-0 inline-flex w-4 h-4 items-center justify-center" aria-hidden="true">
          <span className={cn("w-2 h-2 rounded-full", styles.dot)} />
        </span>
        <span className="flex-1 truncate text-left">{folder.name}</span>
        {hasCounter && (
          <span
            className={cn(
              "ml-2 shrink-0 text-[12px] leading-none tabular-nums",
              isActive ? "text-fg font-medium" : "text-fg-muted",
            )}
          >
            {count > 999 ? "999+" : count}
          </span>
        )}
      </button>

      <button
        ref={menuBtnRef}
        type="button"
        onClick={() => setMenuOpen((p) => !p)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={t("Acciones de la carpeta {{nombre}}").replace("{{nombre}}", folder.name)}
        className="shrink-0 inline-flex items-center justify-center w-11 h-11 md:w-6 md:h-8 rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
      >
        <MoreHorizontal className="w-3.5 h-3.5" aria-hidden="true" />
      </button>

      {menuOpen && (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 top-full mt-1 min-w-[150px] z-30 bg-bg border border-border rounded-md shadow-elevated py-1 animate-scale-in origin-top-right motion-reduce:animate-none"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              onStartRename();
            }}
            className="w-full flex items-center gap-2 px-3 h-8 text-xs text-fg hover:bg-bg-muted text-left"
          >
            <Pencil className="w-3.5 h-3.5 text-fg-muted shrink-0" aria-hidden="true" />
            {t("Renombrar")}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              onRequestDelete();
            }}
            className="w-full flex items-center gap-2 px-3 h-8 text-xs text-status-error hover:bg-bg-muted text-left"
          >
            <Trash2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            {t("Eliminar")}
          </button>
        </div>
      )}
    </div>
  );
}

// Modal chico de confirmación de borrado (patrón del confirm de resolver de
// ConversationDetail). Deja clarísimo que las conversaciones NO se borran.
function DeleteFolderModal({ folder, onCancel, onConfirm, loading, error }) {
  const { t } = useTranslation();
  // A6 · Escape + bloqueo del scroll del body (patrón de ui/Modal): sin el
  // bloqueo la bottom-nav (z-1000) seguía capturando toques encima del diálogo,
  // porque la red de seguridad del shell se activa con
  // `body[style*="overflow: hidden"] .bottom-nav`.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onCancel]);
  if (!folder) return null;
  const count = typeof folder.count === "number" ? folder.count : 0;
  // zIndex 1200 = banda de overlays de src/scss/_bottom-nav.scss:8-26.
  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "var(--overlay-backdrop, rgba(0,0,0,0.5))", zIndex: 1200 }}
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="hd-folder-delete-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-xl bg-bg border border-border shadow-xl p-5"
      >
        <h3 id="hd-folder-delete-title" className="text-base font-semibold text-fg">
          {t('Eliminar carpeta "{{nombre}}"').replace("{{nombre}}", folder.name)}
        </h3>
        <p className="mt-1 text-sm text-fg-muted">
          {t("Las conversaciones no se borran: quedan sin carpeta.")}
          {count > 0 &&
            " " +
              (count === 1
                ? t("1 conversación volverá a la bandeja general.")
                : t("{{n}} conversaciones volverán a la bandeja general.").replace("{{n}}", count))}
        </p>
        {error && (
          <p role="alert" className="mt-2 text-xs text-status-error">
            {error}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-8 px-3 rounded-md text-xs font-medium border border-border text-fg hover:bg-bg-muted transition-colors"
          >
            {t("Cancelar")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center gap-2 h-8 px-3 rounded-md text-xs font-medium bg-status-error text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {loading && (
              <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin motion-reduce:animate-none" />
            )}
            {t("Eliminar")}
          </button>
        </div>
      </div>
    </div>
  );
}

// Sección "Carpetas" completa: lista + crear/renombrar inline + eliminar con
// confirmación. El estado de edición vive acá (no en el rail) para que abrir
// el editor no toque nada más.
function FoldersSection({
  collapsed,
  folders,
  resolvedActive,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
}) {
  const { t } = useTranslation();
  // null | { mode: "create" } | { mode: "rename", folder }
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null); // carpeta a eliminar | null
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleCreate = async (name, color) => {
    await onCreateFolder(name, color);
    setEditing(null);
  };

  const handleRename = async (name, color) => {
    await onRenameFolder(editing.folder.id, name, color);
    setEditing(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await onDeleteFolder(deleting.id);
      setDeleting(null);
    } catch (err) {
      setDeleteError(err?.message || t("No se pudo eliminar la carpeta."));
    } finally {
      setDeleteLoading(false);
    }
  };

  const newFolderRow = (
    <button
      type="button"
      onClick={() => setEditing({ mode: "create" })}
      className="w-full flex items-center gap-2.5 h-11 md:h-8 px-2 rounded-md text-sm text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <span className="shrink-0 inline-flex w-4 h-4 items-center justify-center">
        <Plus className="w-4 h-4" aria-hidden="true" />
      </span>
      <span className="flex-1 truncate text-left">{t("Nueva carpeta")}</span>
    </button>
  );

  return (
    <>
      {folders.map((folder) =>
        editing?.mode === "rename" && editing.folder.id === folder.id && !collapsed ? (
          <FolderEditor
            key={folder.id}
            initialName={folder.name}
            initialColor={folder.color || DEFAULT_FOLDER_COLOR}
            onSave={handleRename}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <FolderRailRow
            key={folder.id}
            folder={folder}
            isActive={resolvedActive === `folder.${folder.id}`}
            collapsed={collapsed}
            onSelect={() => onSelectFolder(folder.id)}
            onStartRename={() => setEditing({ mode: "rename", folder })}
            onRequestDelete={() => {
              setDeleteError(null);
              setDeleting(folder);
            }}
          />
        ),
      )}

      {/* Crear: solo con el rail expandido (colapsado no hay dónde teclear). */}
      {!collapsed &&
        (editing?.mode === "create" ? (
          <FolderEditor onSave={handleCreate} onCancel={() => setEditing(null)} />
        ) : (
          <>
            {folders.length === 0 && (
              // fg-muted, no fg-subtle: es la única explicación de la sección.
              <p className="px-2 text-[11px] text-fg-muted">
                {t("Crea carpetas para ordenar tu bandeja")}
              </p>
            )}
            {newFolderRow}
          </>
        ))}

      {deleting && (
        <DeleteFolderModal
          folder={deleting}
          onCancel={() => setDeleting(null)}
          onConfirm={handleConfirmDelete}
          loading={deleteLoading}
          error={deleteError}
        />
      )}
    </>
  );
}

// ─── Sección con label ───────────────────────────────────────────────────────
function RailSection({ label, collapsed, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      {!collapsed && (
        <div className="px-2 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
          {label}
        </div>
      )}
      {collapsed && <div className="my-1 mx-2 border-t border-border/60" aria-hidden="true" />}
      {children}
    </div>
  );
}

// ─── HelpdeskRail ────────────────────────────────────────────────────────────
// activeKey: clave de la sección activa (override). Si no se pasa, se deriva del pathname.
// counters: { all, unassigned, mine, sla, ai_live, resolved, whatsapp, messenger, instagram, portal, groups }
// onSelectInboxKey(key): callback cuando el usuario elige una sub-view del inbox
//                       (las rutas de Suite navegan por su cuenta vía useNavigate).
// channelAccounts: [{ id, label, phone, instance_name, channel, status, purpose }]
//                       — vacío si el endpoint cae o no hay integraciones, y en
//                       ese caso el rail se ve exactamente como sin cuentas.
//                       `phone`, `status` y `purpose` son opcionales: el backend
//                       los está poblando y la fila degrada sola sin ellos.
// accountFilter: clave (accountKeyOf) de la cuenta filtrada, o null.
// onSelectAccount(key, account|null): elegir cuenta (o "Todas las cuentas").
// folders: carpetas del tenant [{id, name, color, count, ...}] (count viene del
//          backend y excluye resueltas). Los handlers de CRUD son async y
//          RETHROW: el editor inline muestra el 409 (duplicado / tope de 30).
// onSelectFolder(id) / onCreateFolder(name, color) /
// onRenameFolder(id, name, color) / onDeleteFolder(id).
export default function HelpdeskRail({
  navId = "helpdesk-rail-nav",
  activeKey,
  counters = {},
  onSelectInboxKey,
  channelAccounts = [],
  accountFilter = null,
  onSelectAccount,
  folders = [],
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onSelectRetiredAccount,
  onManageAccount,
  collapsed,
  onToggleCollapsed,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();

  // Mismo criterio que QuickRepliesSettings (isSupervisor): configurar el
  // catálogo de respuestas rápidas es tarea de supervisión, no de agente de
  // piso. Un agente que entrara igual vería la pantalla "sin acceso": mejor no
  // ofrecerle la puerta.
  const canConfigure =
    user?.role === roleRoutes.supervisor ||
    user?.role === roleRoutes.admin ||
    user?.role === roleRoutes.superadmin ||
    user?.role === roleRoutes.systemSuperadmin;

  const [expandedChannels, setExpandedChannels] = usePersistentState(
    "rail.expandedChannels",
    {}
  );

  // Determinar activeKey si no vino explícito
  const resolvedActive = (() => {
    if (activeKey) return activeKey;
    if (pathname.startsWith("/admin/helpdesk/contacts")) return "suite.contacts";
    if (pathname.startsWith("/admin/helpdesk/settings")) return "suite.settings";
    if (pathname.startsWith("/admin/helpdesk/quick-replies")) return "suite.quickReplies";
    if (pathname.startsWith("/admin/helpdesk/reports")) return "suite.reports";
    if (pathname.startsWith("/admin/helpdesk/ai")) return "suite.ai";
    // Va DESPUÉS de la de "ai" a propósito: "/admin/helpdesk/interno" no
    // empieza con "/admin/helpdesk/ai", pero mantener el orden deja el
    // isomorfismo con el resto de ramas (todas comparan el mismo prefijo).
    if (pathname.startsWith("/admin/helpdesk/interno")) return "suite.internal";
    return "inbox.unassigned"; // default sensato si estamos en /admin/helpdesk sin override
  })();

  const accountsByKey = useMemo(() => {
    const map = {};
    for (const [key, kinds] of Object.entries(CHANNEL_ACCOUNT_KINDS)) {
      map[key] = Array.isArray(channelAccounts)
        ? channelAccounts.filter((account) => kinds.includes(account?.channel))
        : null;
    }
    return map;
  }, [channelAccounts]);

  // Los canales que dependen de una vinculación (número o página) solo se
  // muestran si el operador tiene alguna. Con las cuentas desconocidas (null:
  // no se pudieron pedir) se muestran todos: esconder un canal vinculado por un
  // fallo de red sería peor que mostrar uno de más.
  const isLinked = (key) =>
    accountsByKey[key] === null || accountsByKey[key].length > 0;

  // Handler para items de inbox/channel: si NO estamos en /admin/helpdesk,
  // navegamos primero ahí y pasamos la sub-view en el state.
  const handleInbox = (key) => {
    const isOnInbox = pathname === "/admin/helpdesk" || pathname === "/admin/helpdesk/";
    if (isOnInbox && onSelectInboxKey) {
      onSelectInboxKey(key);
    } else {
      navigate("/admin/helpdesk", { state: { railKey: key } });
    }
  };

  const handleAccount = (key, account) => {
    if (onSelectAccount) onSelectAccount(key, account);
    else navigate("/admin/helpdesk", { state: { railKey: key } });
  };

  // Carpeta activa = inboxKey 'folder.<id>'. Desde otra ruta del helpdesk se
  // navega al inbox con la clave en el state, igual que las bandejas.
  const handleFolder = (folderId) => {
    const isOnInbox = pathname === "/admin/helpdesk" || pathname === "/admin/helpdesk/";
    if (isOnInbox && onSelectFolder) {
      onSelectFolder(folderId);
    } else {
      navigate("/admin/helpdesk", { state: { railKey: `folder.${folderId}` } });
    }
  };

  // Un canal con cuentas nace desplegado: si arrancara cerrado, el agente
  // seguiría sin ver por qué número atiende hasta descubrir el chevron. Solo se
  // guarda lo que el usuario decidió explícitamente (por eso `?? true`, no un
  // seed del objeto: un `{}` persistido de antes no debe leerse como "cerrado").
  const isChannelExpanded = (key) => expandedChannels?.[key] ?? true;

  const toggleChannel = (key) =>
    setExpandedChannels((prev) => ({ ...prev, [key]: !(prev?.[key] ?? true) }));

  // Vista de un número retirado = inboxKey 'retired.<id>'. Desde otra ruta del
  // helpdesk se navega al inbox con la clave en el state, igual que carpetas.
  const handleRetired = (account) => {
    const isOnInbox = pathname === "/admin/helpdesk" || pathname === "/admin/helpdesk/";
    if (isOnInbox && onSelectRetiredAccount) {
      onSelectRetiredAccount(account);
    } else {
      navigate("/admin/helpdesk", { state: { railKey: `retired.${account.id}` } });
    }
  };

  const channelProps = (key) => ({
    navId,
    channelKey: key,
    accounts: accountsByKey[key] || [],
    expanded: isChannelExpanded(key),
    onToggleExpanded: () => toggleChannel(key),
    accountFilter: resolvedActive === key ? accountFilter : null,
    onSelect: () => handleInbox(key),
    onSelectAccount: handleAccount,
    activeKey: resolvedActive,
    onSelectRetired: handleRetired,
    // Retirar o migrar un número: tarea de supervisión (el backend exige
    // supervisor o administrador), mismo criterio que canConfigure.
    onManageAccount: canConfigure ? onManageAccount : undefined,
    collapsed,
  });

  return (
    <aside
      aria-label={t("Navegación de mesa de ayuda")}
      className={cn(
        "shrink-0 flex flex-col bg-bg border-r border-border/60 h-full overflow-y-auto",
        "transition-[width] duration-200 ease-out motion-reduce:transition-none",
        collapsed ? "w-[64px]" : "w-[224px]",
      )}
    >
      {/* Contenido scrolleable: arranca directo en BANDEJA. El sidebar admin
          ya identifica esta ruta como "Mesa de ayuda", repetirlo aquí era ruido. */}
      <nav id={navId} className="flex-1 overflow-y-auto px-2 pt-2 pb-1">
        <RailSection label={t("Bandeja")} collapsed={collapsed}>
          <RailItem
            icon={Inbox}
            label={t("Todas")}
            isActive={resolvedActive === "inbox.all"}
            counter={counters.all}
            collapsed={collapsed}
            onClick={() => handleInbox("inbox.all")}
          />
          <RailItem
            icon={CircleAlert}
            label={t("Sin asignar")}
            isActive={resolvedActive === "inbox.unassigned"}
            counter={counters.unassigned}
            badgeTone={counters.unassigned > 0 ? "warning" : undefined}
            collapsed={collapsed}
            onClick={() => handleInbox("inbox.unassigned")}
          />
          <RailItem
            icon={UserCheck}
            label={t("Mis casos")}
            isActive={resolvedActive === "inbox.mine"}
            counter={counters.mine}
            collapsed={collapsed}
            onClick={() => handleInbox("inbox.mine")}
          />
          <RailItem
            icon={AlarmClock}
            label={t("Vencidos (SLA)")}
            isActive={resolvedActive === "inbox.sla"}
            counter={counters.sla}
            badgeTone={counters.sla > 0 ? "danger" : undefined}
            collapsed={collapsed}
            onClick={() => handleInbox("inbox.sla")}
          />
          {/* Sin contador a propósito (counters.ai no existe): no hay conteo
              por bandeja en el backend y derivarlo de la lista cargada daría
              un número falso. */}
          <RailItem
            icon={Bot}
            label={t("Atendidas por IA")}
            isActive={resolvedActive === "inbox.ai"}
            collapsed={collapsed}
            onClick={() => handleInbox("inbox.ai")}
          />
          {/* Monitoreo: conversaciones que la IA BYO está respondiendo AHORA.
              Distinto de "Atendidas por IA" (historial: lo que la IA respondió
              o derivó en los últimos 30 días, resueltas incluidas). */}
          <RailItem
            icon={Bot}
            label={t("IA en curso")}
            isActive={resolvedActive === "inbox.ai_live"}
            counter={counters.ai_live}
            collapsed={collapsed}
            onClick={() => handleInbox("inbox.ai_live")}
          />
          {/* Archivo: los casos cerrados salen del resto de bandejas y viven
              acá, para que lo pendiente no se mezcle con lo ya resuelto. */}
          <RailItem
            icon={Archive}
            label={t("Resueltos")}
            isActive={resolvedActive === "inbox.resolved"}
            counter={counters.resolved}
            collapsed={collapsed}
            onClick={() => handleInbox("inbox.resolved")}
          />
        </RailSection>

        <RailSection label={t("Por canal")} collapsed={collapsed}>
          {isLinked("channel.whatsapp") && (
            <RailChannel
              {...channelProps("channel.whatsapp")}
              channelKind="whatsapp"
              label={t("WhatsApp")}
              isActive={resolvedActive === "channel.whatsapp"}
              counter={counters.whatsapp}
            />
          )}
          {isLinked("channel.messenger") && (
            <RailChannel
              {...channelProps("channel.messenger")}
              channelKind="messenger"
              label={t("Messenger")}
              isActive={resolvedActive === "channel.messenger"}
              counter={counters.messenger}
            />
          )}
          {isLinked("channel.instagram") && (
            <RailChannel
              {...channelProps("channel.instagram")}
              channelKind="instagram"
              label={t("Instagram")}
              isActive={resolvedActive === "channel.instagram"}
              counter={counters.instagram}
            />
          )}
          <RailChannel
            {...channelProps("channel.portal")}
            icon={Globe}
            label={t("Portal")}
            isActive={resolvedActive === "channel.portal"}
            counter={counters.portal}
          />
          {/* Canal interno (asistente de gerencia). Va como ítem simple, sin
              sub-árbol de cuentas: no hay número ni página de la que dependa,
              es el hilo del equipo con la IA. Filtra por `channel=internal`,
              que es lo que el inbox ya sabe interpretar. */}
          {showInternalChannelTray(counters) && (
            <RailItem
              icon={Building2}
              label={t("Interno")}
              isActive={resolvedActive === "channel.internal"}
              counter={counters.internal}
              collapsed={collapsed}
              onClick={() => handleInbox("channel.internal")}
            />
          )}
          <RailItem
            icon={Users}
            label={t("Grupos")}
            isActive={resolvedActive === "channel.groups"}
            counter={counters.groups}
            collapsed={collapsed}
            onClick={() => handleInbox("channel.groups")}
          />
        </RailSection>

        {/* Solo el inbox (pages/helpdesk) cablea las carpetas; las vistas
            suite (Reportes, Canales, Contactos, Respuestas rápidas) montan el
            rail sin datos ni handlers. Pintar la sección igual mostraba
            "Crea carpetas..." con carpetas existentes y un "Nueva carpeta"
            cuyo guardar reventaba con onCreateFolder undefined. */}
        {typeof onCreateFolder === "function" && (
          <RailSection label={t("Carpetas")} collapsed={collapsed}>
            <FoldersSection
              collapsed={collapsed}
              folders={folders}
              resolvedActive={resolvedActive}
              onSelectFolder={handleFolder}
              onCreateFolder={onCreateFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
            />
          </RailSection>
        )}

        <RailSection label={t("Suite")} collapsed={collapsed}>
          <RailItem
            icon={Users}
            label={t("Contactos")}
            isActive={resolvedActive === "suite.contacts"}
            collapsed={collapsed}
            onClick={() => navigate("/admin/helpdesk/contacts")}
          />
          <RailItem
            icon={Settings}
            label={t("Canales")}
            isActive={resolvedActive === "suite.settings"}
            collapsed={collapsed}
            onClick={() => navigate("/admin/helpdesk/settings")}
          />
          {canConfigure && (
            <RailItem
              icon={Zap}
              label={t("Respuestas rápidas")}
              isActive={resolvedActive === "suite.quickReplies"}
              collapsed={collapsed}
              onClick={() => navigate("/admin/helpdesk/quick-replies")}
            />
          )}
          <RailItem
            icon={BarChart3}
            label={t("Reportes")}
            isActive={resolvedActive === "suite.reports"}
            collapsed={collapsed}
            onClick={() => navigate("/admin/helpdesk/reports")}
          />
          <RailItem
            icon={Sparkles}
            label={t("Asistente IA")}
            isActive={resolvedActive === "suite.ai"}
            collapsed={collapsed}
            onClick={() => navigate("/admin/helpdesk/ai")}
          />
          {/* Canal interno (asistente de gerencia). Sin el gate canConfigure a
              propósito: la ruta tiene la MISMA puerta adminOnly que "Asistente
              IA" (su vecino en esta sección), así que restringir solo el ítem
              escondería una vista que la URL sí abre. */}
          <RailItem
            icon={Building2}
            label={t("Asistente interno")}
            isActive={resolvedActive === "suite.internal"}
            collapsed={collapsed}
            onClick={() => navigate(INTERNAL_ASSISTANT_PATH)}
          />
        </RailSection>
      </nav>

      {/* Toggle de colapso */}
      <div className="shrink-0 border-t border-border p-2">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-controls={navId}
          aria-label={
            collapsed ? t("Expandir bandeja ([)") : t("Colapsar bandeja ([)")
          }
          title={collapsed ? t("Expandir bandeja ([)") : t("Colapsar bandeja ([)")}
          className={cn(
            "w-full inline-flex items-center gap-2 h-11 md:h-8 px-2 rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
            collapsed && "justify-center px-0",
          )}
        >
          {collapsed ? (
            <PanelLeftOpen className="w-4 h-4" aria-hidden="true" />
          ) : (
            <>
              <PanelLeftClose className="w-4 h-4" aria-hidden="true" />
              <span className="text-xs">{t("Colapsar")}</span>
              <span className="ml-auto text-[11px] text-fg-subtle" aria-hidden="true">
                [
              </span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
